import React, { useContext, useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  TextField,
  Typography,
} from "@mui/material";
import { ReactComponent as FayeIcon } from "../icons/FayeBrand.svg";
import AuthService from "services/auth";
import useIsMobile from "components/hooks/useIsMobile";
import { FayeAppBar } from "components/layout/NavigationLayout";
import { useLocation, useNavigate } from "react-router-dom";
import { SWRegistrationContext } from "components/context/AddServiceWorker";
import LinkRouter from "components/navigation/LinkRouter";
import TwoFactorVerificationMethod from "components/auth/TwoFactorVerificationMethod";
import TwoFactorOTP from "components/auth/TwoFactorOTP";
import { isAxiosError } from "axios";

interface FormValues {
  username: string;
  password: string;
}

type LoginStep = "credentials" | "verification-method" | "otp";

export default () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { subscription } = useContext(SWRegistrationContext);
  // Default to the value in the local state, then the intended path, then null.
  const intendedPath = queryParams.get("destination");
  let from = location.state?.from?.pathname || intendedPath || "/";
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<LoginStep>("credentials");
  const [twoFactorToken, setTwoFactorToken] = useState<string>("");
  const [selectedMethod, setSelectedMethod] = useState<"email" | "sms" | null>(
    null,
  );
  const [methodSelectionError, setMethodSelectionError] = useState<
    string | null
  >(null);
  const [formValues, setFormValues] = useState<FormValues>({
    username: decodeURIComponent(queryParams.get("username") || ""),
    password: "",
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError(false);

    const subscriptionString = subscription
      ? JSON.stringify(subscription.toJSON())
      : null;

    AuthService.login(
      formValues.username,
      formValues.password,
      "advisor",
      subscriptionString,
    ).then(
      (response) => {
        setLoading(false);

        if (!response) {
          return;
        }

        // Check if 2FA is required
        if (response.requiresTwoFactor && response.twoFactorToken) {
          // Store the token for subsequent 2FA requests
          setTwoFactorToken(response.twoFactorToken);
          setFormValues({
            ...formValues,
            password: "", // Clear password from state for security
          });
          // Move to verification method selection
          setStep("verification-method");
        } else if (response.accessToken) {
          // Normal login succeeded (no 2FA required)
          // Send them back to the page they tried to visit when they were
          // redirected to the login page. Use { replace: true } so we don't create
          // another entry in the history stack for the login page.  This means that
          // when they get to the protected page and click the back button, they
          // won't end up back on the login page, which is also really nice for the
          // user experience.
          navigate(from, { replace: true });
        } else if (response.redirectUrl) {
          // Redirect case already handled by AuthService
          return;
        }
      },
      () => {
        setLoading(false);
        setError(true);
      },
    );
  };

  const handleSelectMethod = async (method: "email" | "sms") => {
    setSelectedMethod(method);
    setMethodSelectionError(null);

    try {
      await AuthService.initiateTwoFactor(twoFactorToken, method);
      setStep("otp");
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 429) {
        setMethodSelectionError(
          "Too many attempts. Please try again in a few minutes.",
        );
      } else {
        setMethodSelectionError("Failed to send code. Please try again.");
      }
    }
  };

  const handleVerifyOTP = async (code: string) => {
    const subscriptionString = subscription
      ? JSON.stringify(subscription.toJSON())
      : null;

    const response = await AuthService.verifyTwoFactor(
      twoFactorToken,
      code,
      subscriptionString,
    );
    if (response.redirectUrl) {
      window.location.href = response.redirectUrl;
      return;
    }
    navigate(from, { replace: true });
  };

  const handleResendOTP = async () => {
    try {
      await AuthService.resendTwoFactorCode(twoFactorToken);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 429) {
        throw new Error(
          "Too many attempts. Please try again in a few minutes.",
        );
      }
      throw err;
    }
  };

  const handleCancel = () => {
    setStep("credentials");
    setSelectedMethod(null);
    setError(false);
    setMethodSelectionError(null);
    setTwoFactorToken("");
  };

  const handleBackFromOTP = () => {
    setStep("verification-method");
    setSelectedMethod(null);
    setMethodSelectionError(null);
  };

  useEffect(() => {
    const currentUser = AuthService.getCurrentUser();
    if (currentUser) {
      navigate(intendedPath || "/");
    }
  }, []);

  // Render 2FA verification method selection
  if (step === "verification-method") {
    return (
      <TwoFactorVerificationMethod
        onSelectMethod={handleSelectMethod}
        onCancel={handleCancel}
        error={methodSelectionError}
      />
    );
  }

  // Render 2FA OTP input
  if (step === "otp" && selectedMethod) {
    return (
      <TwoFactorOTP
        method={selectedMethod}
        onVerify={handleVerifyOTP}
        onCancel={handleBackFromOTP}
        onResend={handleResendOTP}
      />
    );
  }

  // Render credentials form (default)
  return (
    <>
      <FayeAppBar />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "500px",
            padding: "0px 28px",
            alignSelf: "stretch",
          }}
        >
          {!isMobile && (
            <div>
              <FayeIcon
                style={{
                  width: "112.762px",
                  height: "64px",
                  flexShrink: "0",
                  marginBottom: "29px",
                }}
              />
            </div>
          )}
          <Typography
            variant="h2Serif"
            sx={{
              marginBottom: "8px",
            }}
          >
            Sign in to Faye
          </Typography>
          {/*<Typography*/}
          {/*  variant="body"*/}
          {/*  sx={{*/}
          {/*    marginBottom: "32px",*/}
          {/*  }}*/}
          {/*>*/}
          {/*  Your business awaits.*/}
          {/*</Typography>*/}
          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            {error && (
              <Alert
                icon={false}
                severity="error"
                sx={{
                  alignSelf: "stretch",
                  marginBottom: "12px",
                  borderRadius: "12px",
                  border: "2px solid #FAD3D0",
                  background: "#FFFBFA",
                }}
              >
                Username or password is incorrect
              </Alert>
            )}
            <FormControl fullWidth style={{ marginTop: 16 }} disabled={loading}>
              <TextField
                sx={{ flexGrow: 1 }}
                disabled={loading}
                autoFocus
                label="Username"
                type="email"
                error={error}
                name="username"
                autoComplete="on"
                value={formValues.username}
                onChange={handleInputChange}
              />
            </FormControl>
            <FormControl fullWidth style={{ marginTop: 16 }} disabled={loading}>
              <TextField
                sx={{ flexGrow: 1 }}
                disabled={loading}
                error={error}
                label="Password"
                name="password"
                type="password"
                autoComplete="on"
                value={formValues.password}
                onChange={handleInputChange}
              />
            </FormControl>
            <Button
              disabled={loading}
              type="submit"
              fullWidth
              style={{ marginTop: "12px" }}
            >
              Sign in
            </Button>
            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"center"}
            >
              <LinkRouter
                to={`/forgot-password${
                  formValues.username
                    ? `?username=${encodeURIComponent(formValues.username)}`
                    : ""
                }`}
              >
                <Button variant="text" sx={{ mt: 1, mr: 1 }}>
                  Forgot password?
                </Button>
              </LinkRouter>
            </Box>
            <Divider sx={{ marginTop: "24px", marginBottom: "24px" }} />
            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"center"}
            >
              <Button
                variant="text"
                sx={{ mt: 1, mr: 1 }}
                onClick={() => {
                  window.location.href = `https://app.findfaye.com/member-login${
                    formValues.username
                      ? `?email=${encodeURIComponent(formValues.username)}`
                      : ""
                  }`;
                }}
              >
                Are you a Faye client? Log in here
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </>
  );
};
